import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    TextField
} from '@mui/material';
import { getAllRoles } from '../../Services/RolesPermissions/RolesPermissionsApi';
import { getPermissionsByRoleId, updateMenuPermission } from '../../Services/RolesPermissions/MenuManagemnet/GetMenusPermissions';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
import { usePermissions } from '../../context/PermissionContext';
import GlobalPagination from '../../Components/Common/Globalpagination';

const MenuAccessControl = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const { invalidatePermissions } = usePermissions();
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState("");
    const [menusData, setMenusData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rolesLoading, setRolesLoading] = useState(true);

    // Pagination and search states
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [totalItems, setTotalItems] = useState(0);

    // Fetch all roles on mount
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                setRolesLoading(true);
                const response = await getAllRoles();
                if (response && response.data) {
                    setRoles(response.data);
                }
            } catch (err) {
                console.error("Failed to fetch roles", err);
                showSnackbar("Failed to load roles.", "error");
            } finally {
                setRolesLoading(false);
            }
        };
        fetchRoles();
    }, [showSnackbar]);

    const fetchMenuAccess = useCallback(async (roleId, currentPage, limit, search) => {
        if (!roleId) return;
        try {
            setLoading(true);
            const response = await getPermissionsByRoleId(roleId, currentPage, limit, search);
            if (response && response.success && response.data) {
                const mappedData = response.data.map(item => ({
                    ...item,
                    edit: item.edit ?? item.update ?? false,
                    remove: item.remove ?? item.delete ?? false
                }));
                setMenusData(mappedData);
                // Safely grab total count from typical API response patterns
                const total = response.totalItems || 
                              response.totalRecords || 
                              response.total || 
                              response.totalCount || 
                              response.pagination?.totalItems || 
                              (response.totalPages ? response.totalPages * rowsPerPage : mappedData.length);
                setTotalItems(total);
            } else {
                setMenusData([]);
                setTotalItems(0);
            }
        } catch (err) {
            console.error(err);
            showSnackbar("Failed to fetch menu access data for this role.", "error");
        } finally {
            setLoading(false);
        }
    }, [rowsPerPage, showSnackbar]);

    useEffect(() => {
        if (selectedRole) {
            fetchMenuAccess(selectedRole, page, rowsPerPage, searchTerm);
        }
    }, [selectedRole, page, rowsPerPage, searchTerm, fetchMenuAccess]);

    // Handle search debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerm !== searchInput) {
                setSearchTerm(searchInput);
                setPage(1);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchInput, searchTerm]);

    const handleRoleChange = (event) => {
        setSelectedRole(event.target.value);
        setPage(1);
        setSearchInput("");
        setSearchTerm("");
    };

    const handleCheckboxChange = async (menuId, field) => {
        if (!selectedRole) {
            showSnackbar("Please select a role first.", "error");
            return;
        }

        const targetMenu = menusData.find(m => m.id === menuId);
        if (!targetMenu) return;

        const oldValue = !!targetMenu[field];
        const newValue = !oldValue;

        // Optimistically update local state
        setMenusData(prevData =>
            prevData.map(menu =>
                menu.id === menuId ? { ...menu, [field]: newValue } : menu
            )
        );

        const updatedMenu = { ...targetMenu, [field]: newValue };
        const permissionData = {
            roleId: Number(selectedRole),
            menuId: updatedMenu.menuId || updatedMenu.id,
            create: !!updatedMenu.create,
            read: !!updatedMenu.read,
            edit: !!updatedMenu.edit,
            remove: !!updatedMenu.remove,
            view: !!updatedMenu.view
        };

        try {
            await updateMenuPermission(permissionData);
            showSnackbar("Permission updated successfully!", "success");
            // Note: We don't call invalidatePermissions() here because:
            // 1. The local state is already updated optimistically above
            // 2. invalidatePermissions() clears the permission cache and menus, causing the page to reset
            // 3. The user can manually refresh if they want to see updated permissions elsewhere
        } catch (err) {
            console.error("Failed to update permission", err);
            // Revert on error
            setMenusData(prevData =>
                prevData.map(menu =>
                    menu.id === menuId ? { ...menu, [field]: oldValue } : menu
                )
            );
            const errMsg = err.response?.data?.message || err.message || "Failed to update permission. Reverted change.";
            showSnackbar(errMsg, "error");
        }
    };

    if (rolesLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography sx={{
                    color: '#222',
                    fontSize: { xs: '1.75rem', md: '1.8rem' },
                    fontWeight: 500
                }}>
                    Menu Access Control
                </Typography>
                <FormControl sx={{ minWidth: 200 }} size="small">
                    <InputLabel id="role-select-label" sx={{ fontSize: '0.85rem' }}>Select Role</InputLabel>
                    <Select
                        labelId="role-select-label"
                        id="role-select"
                        value={selectedRole}
                        label="Select Role"
                        onChange={handleRoleChange}
                        sx={{
                            borderRadius: '6px',
                            height: '40px',
                            fontSize: '0.85rem',
                            '& fieldset': { borderColor: '#E0E0E0' },
                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                        }}
                    >
                        {roles.map((role) => (
                            <MenuItem key={role.id} value={role.id} sx={{ fontSize: '0.85rem' }}>
                                {role.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            {/* Alerts handled by GlobalSnackbar */}

            {!selectedRole ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '8px', border: "1px solid #E8E8E8" }} elevation={0}>
                    <Typography variant="body1" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                        Please select a role from the dropdown above to manage its menu permissions.
                    </Typography>
                </Paper>
            ) : (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search menus..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            sx={{
                                width: 300,
                                bgcolor: 'white',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    height: '40px',
                                    fontSize: '0.85rem',
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                }
                            }}
                        />
                    </Box>
                    <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                        <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Menu Name</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>View</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Create</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Edit</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Remove</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                                <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                            </TableCell>
                                        </TableRow>
                                    ) : menusData.length > 0 ? (
                                        menusData.map((menu) => (
                                            <TableRow key={menu.id} sx={{
                                                transition: 'background-color 0.2s',
                                                '& td': { borderBottom: '1px solid #F0F0F0' },
                                                '&:last-child td': { borderBottom: 0 },
                                            }}>
                                                <TableCell sx={{ fontWeight: 500, py: 1, px: 1.5, fontSize: '0.8rem', color: '#333' }}>{menu.menuName || menu.name || 'N/A'}</TableCell>
                                                <TableCell align="center" sx={{ py: 1, px: 1.5 }}>
                                                    <Checkbox
                                                        checked={!!menu.view}
                                                        onChange={() => handleCheckboxChange(menu.id, 'view')}
                                                        sx={{ color: '#FFBD65', '&.Mui-checked': { color: '#FFBD65' }, p: 0.5 }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center" sx={{ py: 1, px: 1.5 }}>
                                                    <Checkbox
                                                        checked={!!menu.create}
                                                        onChange={() => handleCheckboxChange(menu.id, 'create')}
                                                        sx={{ color: '#FFBD65', '&.Mui-checked': { color: '#FFBD65' }, p: 0.5 }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center" sx={{ py: 1, px: 1.5 }}>
                                                    <Checkbox
                                                        checked={!!menu.edit}
                                                        onChange={() => handleCheckboxChange(menu.id, 'edit')}
                                                        sx={{ color: '#FFBD65', '&.Mui-checked': { color: '#FFBD65' }, p: 0.5 }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center" sx={{ py: 1, px: 1.5 }}>
                                                    <Checkbox
                                                        checked={!!menu.remove}
                                                        onChange={() => handleCheckboxChange(menu.id, 'remove')}
                                                        sx={{ color: '#FFBD65', '&.Mui-checked': { color: '#FFBD65' }, p: 0.5 }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                                No menu access data found for this role
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <GlobalPagination
                            page={page}
                            pageSize={rowsPerPage}
                            totalRecords={totalItems}
                            onPageChange={(newPage) => setPage(newPage)}
                            onPageSizeChange={(newSize) => {
                                setRowsPerPage(newSize);
                                setPage(1);
                            }}
                            loading={loading}
                        />
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default MenuAccessControl;